import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, Chip, Collapse, Divider, Grid,
  Checkbox, IconButton, ListItemText, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import ClearIcon from '@mui/icons-material/Clear'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { CourierScoreCard } from './EngineTab'
import { downloadRowsAsXlsx } from '../../common/exportRows'
import { PACE_PRESETS, presetLabel } from './decisionConfigService'
import {
  FALLBACK_COLUMNS, buildSampleCsv, downloadTextFile, fetchSimulateColumns, fileToCsvText,
  missingRequiredColumns, resultRowsForExport, runExternalShadow,
} from './externalShadowRunService'

const MAX_FILE_MB = 5

// Verticals PACE cohorts the neutral merchant profile by. Free text is allowed —
// an unrecognised vertical just falls back to the all-merchant cohort.
const VERTICALS = [
  'apparel', 'electronics', 'health and wellness', 'beauty and personal care',
  'home and kitchen', 'food and beverage', 'jewellery', 'footwear', 'books and stationery',
]

/**
 * PACE → External Shadow Run.
 *
 * Runs a PROSPECT's order book through the real PACE allocator. Because they are not
 * on proship, PACE has no merchant record, rate card, courier config or history for
 * them — it synthesises the candidate set, each courier's zone and each courier's
 * price from its own lake, then ranks with the production scorer. Everything shown
 * is a network benchmark, not a quote: that distinction is on the screen and in the
 * export, because a prospect will read these numbers as a promise otherwise.
 *
 * Nothing here is persisted — a simulation never lands in the PACE result table and
 * never counts toward shadow agreement.
 */
export default function ExternalShadowRunTab() {
  const { enqueueSnackbar } = useSnackbar()
  const fileInput = useRef(null)

  const [columns, setColumns] = useState(FALLBACK_COLUMNS)
  const [file, setFile] = useState(null)
  const [csvText, setCsvText] = useState('')
  const [vertical, setVertical] = useState('')
  // PACE+ #4: also rank every order under these presets for a side-by-side comparison
  const [presets, setPresets] = useState([])
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  // Keep the sample sheet in step with the parser. If the proxy isn't deployed
  // yet the local fallback is used — the sample still downloads.
  useEffect(() => {
    let alive = true
    fetchSimulateColumns()
      .then((res) => {
        const cols = res?.data?.columns
        if (alive && Array.isArray(cols) && cols.length) setColumns(cols)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const onDownloadSample = () => {
    downloadTextFile('pace-external-shadow-run-sample.csv', buildSampleCsv(columns))
    enqueueSnackbar('Sample sheet downloaded', { variant: 'success' })
  }

  const onPickFile = async (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''            // let the same file be re-selected after an edit
    if (!f) return
    setError(''); setResult(null)
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`File is larger than ${MAX_FILE_MB} MB. Upload a representative sample instead.`)
      return
    }
    try {
      const text = await fileToCsvText(f)
      const missing = missingRequiredColumns(text)
      if (missing.length) {
        setError(`Missing required column(s): ${missing.join(', ')}. Start from the sample sheet.`)
        setFile(null); setCsvText('')
        return
      }
      setFile(f); setCsvText(text)
    } catch (err) {
      setError(`Could not read the file: ${err.message || err}`)
    }
  }

  const onRun = async () => {
    if (!csvText) return
    setRunning(true); setError(''); setResult(null)
    try {
      const res = await runExternalShadow({
        csv_text: csvText,
        product_vertical: vertical || undefined,
        ...(presets.length ? { presets } : {}),
      })
      const body = res?.data
      if (body?.error) { setError(body.error); return }
      setResult(body)
      const { scored = 0, uploaded = 0 } = body?.counts || {}
      enqueueSnackbar(`Scored ${scored} of ${uploaded} orders`, { variant: 'success' })
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'The run failed.')
    } finally {
      setRunning(false)
    }
  }

  const onClear = () => {
    setFile(null); setCsvText(''); setResult(null); setError('')
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>What this is</AlertTitle>
        Scores a <strong>prospect&apos;s</strong> orders through the live PACE allocator. They have
        nothing in proship, so the courier shortlist, zones and prices are reconstructed from
        Prozo&apos;s own network history. Results are <strong>benchmark estimates, not a quote</strong>,
        and nothing is saved against a merchant.
      </Alert>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>1 · Download the sample sheet</Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            Four columns are required — <strong>pickup_pincode, drop_pincode, weight_g, payment_mode</strong>.
            Payment mode matters more than it looks: it selects the courier&apos;s COD-vs-prepaid track
            record, which is the single largest input to the risk scoring. Fill{' '}
            <strong>current_courier</strong>, <strong>current_price_paid</strong> and{' '}
            <strong>current_outcome</strong> too — without them there is nothing to compare against.
          </Typography>
          <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />}
                  onClick={onDownloadSample}>
            Download sample CSV
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>2 · Upload the filled sheet</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <input ref={fileInput} type="file" accept=".csv,.txt,.xlsx,.xls"
                   style={{ display: 'none' }} onChange={onPickFile} />
            <Button size="small" variant="outlined" startIcon={<UploadFileOutlinedIcon />}
                    onClick={() => fileInput.current?.click()}>
              Choose file
            </Button>
            {file && (
              <Chip size="small" label={file.name} onDelete={onClear} deleteIcon={<ClearIcon />} />
            )}
            <TextField
              select size="small" label="Product vertical" value={vertical}
              onChange={(e) => setVertical(e.target.value)} sx={{ minWidth: 230 }}
              helperText="Picks the comparable-merchant cohort"
            >
              <MenuItem value=""><em>Not specified</em></MenuItem>
              {VERTICALS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="Compare presets" value={presets} sx={{ minWidth: 260 }}
              SelectProps={{
                multiple: true,
                renderValue: (sel) => (sel.length ? sel.map(presetLabel).join(', ') : ''),
              }}
              onChange={(e) => setPresets(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              helperText="Optional — also ranks every order under each preset"
            >
              {PACE_PRESETS.map((p) => (
                <MenuItem key={p.key} value={p.key} dense>
                  <Checkbox size="small" checked={presets.includes(p.key)} />
                  <ListItemText primary={p.label} secondary={p.description}
                                secondaryTypographyProps={{ sx: { maxWidth: 320, whiteSpace: 'normal' } }} />
                </MenuItem>
              ))}
            </TextField>
            <LoadingButton size="small" variant="contained" loading={running}
                           disabled={!csvText} onClick={onRun}>
              Run external shadow
            </LoadingButton>
          </Stack>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            CSV or Excel, up to {MAX_FILE_MB} MB. COD share is measured from the sheet itself.
          </Typography>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {result && <Results result={result} enqueueSnackbar={enqueueSnackbar} />}
    </Box>
  )
}

// ── results ────────────────────────────────────────────────────────────────
function Results({ result, enqueueSnackbar }) {
  const counts = result.counts || {}
  const agg = result.aggregate || {}
  const cmp = agg.comparison
  const basis = result.merchant_basis || {}

  const onExport = () => {
    const rows = resultRowsForExport(result)
    if (!rows.length) { enqueueSnackbar('Nothing to export', { variant: 'warning' }); return }
    downloadRowsAsXlsx('pace-external-shadow-run.xlsx', rows, 'Orders')
  }

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        {result.disclaimer}
      </Alert>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Stat label="Orders uploaded" value={counts.uploaded} />
        <Stat label="Scored" value={counts.scored} color="success.main" />
        <Stat label="No priceable courier" value={counts.unscoreable}
              hint="Couriers serve the lane but we have no rate-card evidence for that zone/weight, so no price is shown rather than a guessed one." />
        <Stat label="Rejected rows" value={counts.rejected} color={counts.rejected ? 'error.main' : undefined} />
        <Stat label="Avg PACE quote" value={agg.avg_pace_quote != null ? `₹${agg.avg_pace_quote}` : '—'} />
      </Grid>

      {cmp && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>Versus what they do today</Typography>
            <Grid container spacing={1.5}>
              <Stat label="Orders compared" value={cmp.orders_with_current_courier} />
              <Stat label="Same courier as today" value={`${cmp.same_courier_as_today} (${cmp.agreement_pct ?? '—'}%)`} />
              <Stat label="Different courier" value={cmp.different_courier} color="primary.main" />
              <Stat label="Avg price delta" value={cmp.avg_price_delta != null ? `₹${cmp.avg_price_delta}` : '—'}
                    color={cmp.avg_price_delta < 0 ? 'success.main' : 'text.primary'} />
              <Stat label="Total on this sample" value={cmp.total_price_delta != null ? `₹${cmp.total_price_delta}` : '—'}
                    color={cmp.total_price_delta < 0 ? 'success.main' : 'text.primary'} />
            </Grid>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Courier matching is by name and is approximate — their &ldquo;Delhivery&rdquo; may be any of
              several Prozo courier accounts. Price deltas compare our benchmark rate against what they
              say they paid, so treat them as indicative.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Scored against</Typography>
            <Chip size="small" variant="outlined"
                  label={`${basis.product_vertical || 'any vertical'} · COD band ${basis.cod_band || '—'}`} />
            <Chip size="small" variant="outlined" label={`${basis.n_merchants || 0} comparable merchants`} />
            <Chip size="small" variant="outlined" label={`model ${result.model_run || '—'}`} />
            <Chip size="small" variant="outlined"
                  label={`COD share ${basis.cod_share_used != null ? `${Math.round(basis.cod_share_used * 100)}%` : '—'}`} />
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={onExport}>
              Export orders
            </Button>
          </Stack>
          {!!(agg.courier_mix || []).length && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Courier mix PACE would use
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {agg.courier_mix.map((c) => (
                  <Chip key={c.prozo_id} size="small"
                        label={`${c.prozo_id} · ${c.orders} (${Math.round((c.share || 0) * 100)}%)`} />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {result.preset_comparison && <PresetComparison cmp={result.preset_comparison} />}

      <OrderTable rows={result.rows || []} />

      {!!(result.unscoreable || []).length && (
        <ProblemTable title="Orders with no priceable courier" rows={result.unscoreable}
                      render={(r) => r.reason} />
      )}
      {!!(result.rejected || []).length && (
        <ProblemTable title="Rejected rows" rows={result.rejected} error
                      render={(r) => (r.errors || []).join('; ')} />
      )}
    </Box>
  )
}

// PACE+ #4 — the same orders ranked under each preset: who it would pick, what that
// costs / how fast / how reliable, and how many orders it moves off the base pick.
const pct = (v) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`)

function PresetComparison({ cmp }) {
  const keys = ['base', ...Object.keys(cmp).filter((k) => k !== 'base')]
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>Preset comparison</Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Same orders, same predictions and prices — only the ranking knobs change. &ldquo;Base&rdquo; is
          PACE&apos;s default (global) business knobs. Averages are over each preset&apos;s own picks.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Preset</TableCell>
                <TableCell align="right">Orders</TableCell>
                <TableCell align="right">Changed vs base</TableCell>
                <TableCell align="right">Avg quote</TableCell>
                <TableCell align="right">Avg ETA (h)</TableCell>
                <TableCell align="right">Delivery %</TableCell>
                <TableCell align="right">FASR %</TableCell>
                <TableCell align="right">FATA %</TableCell>
                <TableCell>Top couriers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((k) => {
                const s = cmp[k] || {}
                return (
                  <TableRow key={k} hover>
                    <TableCell sx={{ fontWeight: k === 'base' ? 400 : 600 }}>
                      {k === 'base' ? 'Base (business knobs)' : presetLabel(k)}
                    </TableCell>
                    <TableCell align="right">{s.orders ?? '—'}</TableCell>
                    <TableCell align="right">
                      {s.orders_changed_vs_base == null ? '—'
                        : `${s.orders_changed_vs_base} (${s.orders ? Math.round((100 * s.orders_changed_vs_base) / s.orders) : 0}%)`}
                    </TableCell>
                    <TableCell align="right">{s.avg_quote != null ? `₹${s.avg_quote.toFixed(2)}` : '—'}</TableCell>
                    <TableCell align="right">{s.avg_eta_h != null ? s.avg_eta_h.toFixed(1) : '—'}</TableCell>
                    <TableCell align="right">{pct(s.avg_delivery)}</TableCell>
                    <TableCell align="right">{pct(s.avg_fasr)}</TableCell>
                    <TableCell align="right">{pct(s.avg_fata)}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {(s.courier_mix || []).slice(0, 3)
                          .map((c) => `${c.prozo_id} ${Math.round((c.share || 0) * 100)}%`).join(' · ') || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}

function Stat({ label, value, color, hint }) {
  const body = (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
        <Typography variant="h6" sx={{ color: color || 'text.primary' }}>{value ?? '—'}</Typography>
      </CardContent>
    </Card>
  )
  return (
    <Grid item xs={6} sm={4} md={2.4}>
      {hint ? <Tooltip title={hint}>{body}</Tooltip> : body}
    </Grid>
  )
}

function OrderTable({ rows }) {
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const paged = useMemo(() => rows.slice(page * size, page * size + size), [rows, page, size])
  if (!rows.length) return null
  return (
    <Card variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Lane</TableCell>
              <TableCell>Weight</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>PACE pick</TableCell>
              <TableCell align="right">Quote</TableCell>
              <TableCell>Zone</TableCell>
              <TableCell>Today</TableCell>
              <TableCell align="right">Delta</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((r) => <OrderRow key={r.line} row={r} />)}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div" count={rows.length} page={page} rowsPerPage={size}
        onPageChange={(_e, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0) }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Card>
  )
}

function OrderRow({ row }) {
  const [open, setOpen] = useState(false)
  const pick = row.pick || {}
  const si = pick.simulated_inputs || {}
  const cmp = row.comparison || {}
  const delta = cmp.price_delta
  return (
    <Fragment>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <TableCell>
          <IconButton size="small" sx={{ pointerEvents: 'none' }}>
            {open ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{row.input?.pickup_pincode} → {row.input?.drop_pincode}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.resolution?.candidates} candidates · {row.resolution?.priced} priced
          </Typography>
        </TableCell>
        <TableCell>
          {row.weight?.final_weight_g != null ? (
            <Tooltip title={`Priced on ${row.weight.weight_source === 'supplied' ? 'the supplied billed weight' : 'max(dead, L×B×H/5000)'} — dead ${row.weight.dead_weight_g ?? '—'} g, volumetric ${row.weight.volumetric_weight_g ?? '—'} g`}>
              <span>{(row.weight.final_weight_g / 1000).toFixed(2)} kg</span>
            </Tooltip>
          ) : row.input?.dead_weight_kg != null ? `${row.input.dead_weight_kg} kg` : '—'}
        </TableCell>
        <TableCell>{row.input?.payment_mode || '—'}</TableCell>
        <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{pick.prozo_id || '—'}</Typography></TableCell>
        <TableCell align="right">{pick.quote != null ? `₹${pick.quote}` : '—'}</TableCell>
        <TableCell>
          {si.zone ? (
            <Tooltip title={`Zone from ${si.zone_source}; price from ${si.price_source} (n=${si.price_support_n})`}>
              <Chip size="small" variant="outlined" label={si.zone} />
            </Tooltip>
          ) : '—'}
        </TableCell>
        <TableCell>
          {cmp.current_courier ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption">{cmp.current_courier}</Typography>
              {cmp.same_courier === false && <Chip size="small" color="primary" variant="outlined" label="switch" />}
            </Stack>
          ) : '—'}
        </TableCell>
        <TableCell align="right">
          {delta == null ? '—' : (
            <Typography variant="body2" sx={{ color: delta < 0 ? 'success.main' : 'error.main' }}>
              {delta < 0 ? '' : '+'}₹{delta}
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Every courier PACE considered for this order, best first. Click one for the full
                score breakdown. Greyed couriers serve the lane but could not be priced.
              </Typography>
              <Stack spacing={0.75}>
                {(row.ranked || []).map((c, i) => (
                  <CourierScoreCard key={c.prozo_id} c={c} rank={i + 1}
                                    isV2Top={c.prozo_id === pick.prozo_id} />
                ))}
              </Stack>
              {row.presets && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">Under presets:</Typography>
                  {Object.entries(row.presets).map(([k, p]) => (
                    <Chip key={k} size="small" variant="outlined"
                          color={p && p.prozo_id !== pick.prozo_id ? 'primary' : 'default'}
                          label={`${presetLabel(k)} → ${p ? `${p.prozo_id}${p.quote != null ? ` ₹${p.quote}` : ''}` : '—'}`} />
                  ))}
                </Stack>
              )}
              {!!(row.warnings || []).length && (
                <Alert severity="warning" sx={{ mt: 1 }}>{row.warnings.join(' · ')}</Alert>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  )
}

function ProblemTable({ title, rows, render, error }) {
  const [open, setOpen] = useState(false)
  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ cursor: 'pointer' }}
               onClick={() => setOpen((o) => !o)}>
          <Typography variant="subtitle2" sx={{ color: error ? 'error.main' : 'text.primary' }}>
            {title} ({rows.length})
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" sx={{ pointerEvents: 'none' }}>
            {open ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
          </IconButton>
        </Stack>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell width={90}>CSV row</TableCell><TableCell>Reason</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.line}>
                    <TableCell>{r.line}</TableCell>
                    <TableCell><Typography variant="caption">{render(r)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </CardContent>
    </Card>
  )
}
